import React, { useEffect, useRef } from 'react';
import { Container, Button } from 'react-bootstrap';
import Marquee from 'react-fast-marquee';
import './HeroSection.css';
import { SparkleButton } from './SparkleEffect';
import BenefitsSection from './BenefitsSection'; // Import BenefitsSection

/*
Here is the visual order of props in the HeroSection component as they appear on the page:
  1. Pre-headline
  2. Title
  3. Subtitle
  4. CTA button (moved here)
  5. Features
  6. Benefits section (with benefitsSectionTitle)
  7. Social proof / logos
*/
const HeroSection = ({
  preHeadline,
  title,
  highlightedTitleText,
  subtitle,
  features,
  ctaText,
  noRiskText,
  showCountdownTimer = true,
  customerCount,
  logoImages = [],
  onCtaClick,
  CountdownTimer,
  // Add customizable margin props with default values
  marginTop = '0rem',
  marginBottom = '0rem',
  paddingTop = '0rem',
  paddingBottom = '0rem',
  benefitsData, // New prop for benefits data
  benefitsSectionTitle, // New prop for benefits section title
  children // Content to render between CTA and benefits (e.g., video section)
}) => {
  // Determine if we should show the CTA button
  const showCTAButton = ctaText && onCtaClick;
  
  const logosContainerRef = useRef(null);

  useEffect(() => {
    const logosContainer = logosContainerRef.current;
    if (!logosContainer) return;

    const logoElements = logosContainer.querySelectorAll('.logo-item');
    if (logoElements.length === 0) return;

    logoElements.forEach(el => {
      const img = el.querySelector('img');
      if (!img) return;

      const handleMouseEnter = () => {
        el.isHovered = true;
      };
      
      const handleMouseLeave = () => {
        el.isHovered = false;
      };

      el.addEventListener('mouseenter', handleMouseEnter);
      el.addEventListener('mouseleave', handleMouseLeave);

      el._cleanupListeners = () => {
        el.removeEventListener('mouseenter', handleMouseEnter);
        el.removeEventListener('mouseleave', handleMouseLeave);
      };
    });

    let animationFrameId;

    const updateLogoStyles = () => {
      const containerRect = logosContainer.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      logoElements.forEach(logoEl => {
        if (!logoEl) return;
        const img = logoEl.querySelector('img');
        if (!img) return;

        if (logoEl.isHovered) {
          if (!logoEl.stylesClearedForHover) {
            img.style.transform = '';
            img.style.opacity = '';
            img.style.filter = '';
            logoEl.stylesClearedForHover = true;
          }
          return;
        }
        
        logoEl.stylesClearedForHover = false;

        const logoRect = logoEl.getBoundingClientRect();
        const logoCenter = logoRect.left + logoRect.width / 2;
        const distance = Math.abs(containerCenter - logoCenter);
        
        const focusWidth = containerRect.width / 5;
        const strength = Math.exp(-Math.pow(distance / focusWidth, 2));
        
        const grayscale = 100 * (1 - strength);
        const opacity = 0.7 + 0.3 * strength;
        const scale = 1 + 0.1 * strength;

        img.style.filter = `grayscale(${grayscale}%)`;
        img.style.opacity = `${opacity}`;
        img.style.transform = `scale(${scale})`;
      });

      animationFrameId = requestAnimationFrame(updateLogoStyles);
    };

    animationFrameId = requestAnimationFrame(updateLogoStyles);

    return () => {
      cancelAnimationFrame(animationFrameId);
      logoElements.forEach(el => {
        if (el && el._cleanupListeners) {
          el._cleanupListeners();
          delete el._cleanupListeners;
          const img = el.querySelector('img');
          if (img) {
            img.style.transform = '';
            img.style.opacity = '';
            img.style.filter = '';
          }
        }
      });
    };
  }, [logoImages]);
  
  // Create a style object for custom margins
  const heroSectionStyle = {
    paddingTop,
    paddingBottom,
    marginTop,
    marginBottom
  };
  
  return (
    <header className="hero-section" style={heroSectionStyle}>
      <Container>
        <div className="hero-content">
          {preHeadline && <span className="pre-headline">{preHeadline}</span>}
          
          <h1 className="hero-title">
            {title} <br />
            {highlightedTitleText && <span className="highlight">{highlightedTitleText}</span>}
          </h1>
          
          {subtitle && <p className="hero-subtitle">{subtitle}</p>}

          {ctaText && (
            <div className="hero-cta-container">
              {showCTAButton && (
                <SparkleButton className="sparkle-button-cta" onClick={onCtaClick}>
                  {ctaText}
                </SparkleButton>
              )}
              {noRiskText && <p className="no-risk">{noRiskText}</p>}
              {showCountdownTimer && CountdownTimer && <CountdownTimer />}
            </div>
          )}

          {features && features.length > 0 && (
            <div className="hero-features">
              {features.map((feature, index) => (
                <div key={index} className="hero-feature">
                  {feature.icon}
                  {feature.text}
                </div>
              ))}
            </div>
          )}

          {/* Render children between CTA/features and benefits (e.g., video section) */}
          {children}

          {/* Render BenefitsSection if data is provided */}
          {benefitsData && benefitsData.length > 0 && (
            <div className="hero-benefits-wrapper"> {/* Optional wrapper for styling */}
              <BenefitsSection
                title={benefitsSectionTitle}
                benefits={benefitsData}
                isInlineInHero={true} // Pass the new prop
              />
            </div>
          )}

          {(logoImages.length > 0 || customerCount) && (
            <div className="social-proof">
              {customerCount && (
                <p className="customers-count">
                  <span className="highlight-number">{customerCount}</span> traders trust our platform on multiple exchanges
                </p>
              )}
              {logoImages.length > 0 && (
                <div className="customer-logos" ref={logosContainerRef}>
                  <Marquee
                    gradient={false}
                    speed={60}
                    pauseOnHover={true}
                    pauseOnClick={true}
                    direction="left"
                  >
                    {logoImages.map((logo, index) => (
                      <div key={index} className="logo-item">
                        <img src={logo.src} alt={logo.alt} width={logo.width} height={logo.height}/>
                      </div>
                    ))}
                  </Marquee>
                </div>
              )}
            </div>
          )}
        </div>
      </Container>
    </header>
  );
};

export default HeroSection;
